import { createSlice } from '@reduxjs/toolkit';

const STORAGE_KEY = 'ebookCart';

const emptyState = {
  items: [],
  totalItems: 0,
  totalPrice: 0,
};

const calculateTotals = (items) => {
  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  return { totalItems, totalPrice };
};

function loadInitial() {
  try {
    const raw = typeof window !== 'undefined' && window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyState;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.items)) return emptyState;
    // Recalcula totais a partir dos items para não confiar em campos derivados antigos
    const totals = calculateTotals(parsed.items);
    return { items: parsed.items, ...totals };
  } catch {
    return emptyState;
  }
}

const initialState = loadInitial();

const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addToCart: (state, action) => {
      // Produtos são digitais: garantir quantidade máxima 1
      const qtyToAdd = action.payload && action.payload.quantity ? action.payload.quantity : 1;
      const existingItem = state.items.find((item) => item.id === action.payload.id);

      if (existingItem) {
        existingItem.quantity = Math.min(existingItem.quantity + qtyToAdd, 1);
      } else {
        state.items.push({
          ...action.payload,
          quantity: Math.min(qtyToAdd, 1),
          addedAt: Date.now(),
        });
      }

      const totals = calculateTotals(state.items);
      state.totalItems = totals.totalItems;
      state.totalPrice = totals.totalPrice;
    },

    removeFromCart: (state, action) => {
      state.items = state.items.filter((item) => item.id !== action.payload);

      const totals = calculateTotals(state.items);
      state.totalItems = totals.totalItems;
      state.totalPrice = totals.totalPrice;
    },

    updateQuantity: (state, action) => {
      const item = state.items.find((item) => item.id === action.payload.id);

      if (item) {
        // Quantidade mínima 1 para produtos digitais; remover se 0 solicitado
        if (action.payload.quantity <= 0) {
          state.items = state.items.filter((item) => item.id !== action.payload.id);
        } else {
          item.quantity = Math.min(action.payload.quantity, 1);
        }
      }

      const totals = calculateTotals(state.items);
      state.totalItems = totals.totalItems;
      state.totalPrice = totals.totalPrice;
    },

    updateItemPrice: (state, action) => {
      const item = state.items.find((item) => item.id === action.payload.id);

      if (item) {
        item.price = action.payload.price;
      }

      const totals = calculateTotals(state.items);
      state.totalItems = totals.totalItems;
      state.totalPrice = totals.totalPrice;
    },

    clearCart: (state) => {
      state.items = [];
      state.totalItems = 0;
      state.totalPrice = 0;
    },

    loadFromLocalStorage: (state, action) => {
      return action.payload;
    },
  },
});

export const { addToCart, removeFromCart, updateQuantity, updateItemPrice, clearCart, loadFromLocalStorage } =
  cartSlice.actions;

export default cartSlice.reducer;
